import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { create } from 'zustand';
import { meQuery, useUpdateSettings } from '@/shared/api/auth';

/**
 * "Menyuni yopish": the collapsed state lives in the user's settings (`users.settings.sidebarCollapsed`), updated
 * optimistically so the rail switches instantly.
 */
export function useSidebarCollapsed(): [boolean, (collapsed: boolean) => void] {
  const qc = useQueryClient();
  const { data: me } = useQuery(meQuery);
  const update = useUpdateSettings();
  const collapsed = me?.user.settings.sidebarCollapsed ?? false;
  const setCollapsed = (value: boolean) => {
    const prev = qc.getQueryData(meQuery.queryKey);
    qc.setQueryData(meQuery.queryKey, (m) =>
      m ? { ...m, user: { ...m.user, settings: { ...m.user.settings, sidebarCollapsed: value } } } : m,
    );
    update.mutate({ sidebarCollapsed: value }, { onError: () => qc.setQueryData(meQuery.queryKey, prev) });
  };
  return [collapsed, setCollapsed];
}

/** Groups closed by default: the long constructor list and the global "Sozlamalar" group. */
const CLOSED_BY_DEFAULT = new Set(['constructor', 'settings']);

interface GroupState {
  userId: string | null;
  /** explicit user choices; ids missing here use the default */
  open: Record<string, boolean>;
  /** tenant of the last tenant page — kept expanded on user-level pages (settings, guide) */
  lastTenant: string | null;
}

const storageKey = (userId: string) => `atnt.sidebar.${userId}`;

export const useSidebarGroups = create<GroupState>()(() => ({ userId: null, open: {}, lastTenant: null }));

useSidebarGroups.subscribe((s, prev) => {
  if (!s.userId || (s.open === prev.open && s.lastTenant === prev.lastTenant)) return;
  try {
    localStorage.setItem(storageKey(s.userId), JSON.stringify({ open: s.open, lastTenant: s.lastTenant }));
  } catch {
    /* not persisted */
  }
});

export function bindSidebarUser(userId: string) {
  if (useSidebarGroups.getState().userId === userId) return;
  let open: Record<string, boolean> = {};
  let lastTenant: string | null = null;
  try {
    const raw = localStorage.getItem(storageKey(userId));
    const data = raw ? (JSON.parse(raw) as { open?: unknown; lastTenant?: unknown }) : null;
    if (data?.open && typeof data.open === 'object' && !Array.isArray(data.open)) {
      open = Object.fromEntries(Object.entries(data.open).filter(([, v]) => typeof v === 'boolean')) as Record<string, boolean>;
    }
    if (typeof data?.lastTenant === 'string') lastTenant = data.lastTenant;
  } catch {
    /* defaults */
  }
  useSidebarGroups.setState({ userId, open, lastTenant });
}

export function rememberTenant(slug: string) {
  if (useSidebarGroups.getState().lastTenant !== slug) useSidebarGroups.setState({ lastTenant: slug });
}

// own properties only: a group id like "constructor" must not resolve to Object.prototype members
export const isGroupOpen = (open: Record<string, boolean>, id: string) =>
  Object.hasOwn(open, id) ? open[id] === true : !CLOSED_BY_DEFAULT.has(id);

export function setGroupOpen(id: string, value: boolean) {
  useSidebarGroups.setState((s) => (isGroupOpen(s.open, id) === value ? s : { open: { ...s.open, [id]: value } }));
}

/**
 * Open state of a sidebar group. While the current page belongs to the group (`inside`) it is open, and a toggle
 * only lasts for that visit; elsewhere it follows the user's saved choice (toggles are persisted).
 */
export function useGroupOpen(id: string, inside: boolean): [open: boolean, toggle: () => void] {
  const saved = useSidebarGroups((s) => isGroupOpen(s.open, id));
  const [visit, setVisit] = useState<{ inside: boolean; open: boolean | null }>({ inside, open: null });
  if (visit.inside !== inside) setVisit({ inside, open: null });
  const open = (visit.inside === inside ? visit.open : null) ?? (inside || saved);
  const toggle = () => (inside ? setVisit({ inside, open: !open }) : setGroupOpen(id, !open));
  return [open, toggle];
}
